import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IVehicle } from "@app/interfaces/IVehicle";
import { VehicleService } from "@app/services/vehicle.service";
import { lastValueFrom } from "rxjs";
import { IDiagnosticRecord } from "./_models/IDiagnosticRecord";
import { IDiagnosticTroubleCode } from "./_models/IDiagnosticTroubleCode";
import { IVehicleDiagnosticRecords } from "./_models/IVehicleDiagnosticRecords";

@Injectable({ providedIn: 'root' })
export class FaultCodesService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly vehicleService: VehicleService
    ) { }

    getDiagnosticTroubleCodes(): Promise<IDiagnosticTroubleCode[]> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 60 * 24;

        return lastValueFrom(this.httpClient.get<IDiagnosticTroubleCode[]>("/api/vehiclediagnostics/codes", options));
    }

    getReeferDiagnosticTroubleCodes(): Promise<IDiagnosticTroubleCode[]> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 60 * 24;

        return lastValueFrom(this.httpClient.get<IDiagnosticTroubleCode[]>("/api/vehiclediagnostics/codes/reefer", options));
    }

    getLastDiagnosticRecords(invalidateCache = false): Promise<IDiagnosticRecord[]> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<IDiagnosticRecord[]>("/api/vehiclediagnostics/last", options));
    }

    getActiveDiagnosticRecords(invalidateCache = false): Promise<IDiagnosticRecord[]> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<IDiagnosticRecord[]>("/api/vehiclediagnostics/active", options));
    }

    getActiveDiagnosticRecordsForVehicle(vehicleId: number, invalidateCache = false): Promise<IDiagnosticRecord[]> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<IDiagnosticRecord[]>(`/api/vehiclediagnostics/active/${vehicleId}`, options));
    }

    getDiagnosticRecordsForVehicle(vehicleId: number, invalidateCache = false): Promise<IDiagnosticRecord[]> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<IDiagnosticRecord[]>(`/api/vehiclediagnostics/${vehicleId}`, options));
    }

    getLastAndActiveDiagnosticRecords(invalidateCache = false): Promise<IVehicleDiagnosticRecords[]> {
        const vehiclePromise = this.vehicleService.getVehicles() as Promise<IVehicle[]>;
        const activeRecordsPromise = this.getActiveDiagnosticRecords(invalidateCache);
        const lastRecordsPromise = this.getLastDiagnosticRecords(invalidateCache);

        return Promise
            .all([vehiclePromise, activeRecordsPromise, lastRecordsPromise])
            .then(([vehicles, activeRecords, lastRecords]) => {
                const vehicleDiagnosticRecords: IVehicleDiagnosticRecords[] = [];

                vehicles.forEach(vehicle => {
                    let vehicleRecord = vehicleDiagnosticRecords.find(x => x.vehicle.Id === vehicle.Id)

                    if (!vehicleRecord) {
                        vehicleRecord = this.getEmptyDiagnosticRecords(vehicle)
                        vehicleDiagnosticRecords.push(vehicleRecord);
                    }

                    this.findAndAddRecordsForVehicle(vehicleRecord, activeRecords);
                    this.findAndAssignLastRecord(vehicleRecord, lastRecords)
                });

                return vehicleDiagnosticRecords;
            })
    }

    getLastAndActiveDiagnosticRecordsForVehicle(vehicleId: number, invalidateCache = false): Promise<IVehicleDiagnosticRecords> {
        const vehiclePromise = this.vehicleService.getVehicle(vehicleId) as Promise<IVehicle>;
        const activeRecordsPromise = this.getActiveDiagnosticRecords(invalidateCache);
        const lastRecordsPromise = this.getLastDiagnosticRecords(invalidateCache);

        return Promise
            .all([vehiclePromise, activeRecordsPromise, lastRecordsPromise])
            .then(([vehicle, activeRecords, lastRecords]) => {
                const vehicleRecord = this.getEmptyDiagnosticRecords(vehicle);

                this.findAndAddRecordsForVehicle(vehicleRecord, activeRecords);
                this.findAndAssignLastRecord(vehicleRecord, lastRecords)

                return vehicleRecord;
            })
    }

    getVehicleDiagnosticRecords(vehicleId: number, invalidateCache = false): Promise<IDiagnosticRecord[]> {
        const codesPromise = this.getDiagnosticTroubleCodes();
        const recordPromise = this.getDiagnosticRecordsForVehicle(vehicleId, invalidateCache);

        return Promise
            .all([codesPromise, recordPromise])
            .then(([codes, records]) => {
                var codeMap = new Map(codes.map(code => [code.Id, code]));

                return records.map(record => {
                    const code = codeMap.get(record.DiagnosticTroubleCodeId);
                    record.description = code ? code.Description : null;

                    record.FriendlyCode = this.reformatSPNCode(record.FriendlyCode);

                    return record;
                })
            });
    }

    reformatSPNCode(input: string): string {
        if (input.indexOf("(") != 0) {
            return input;
        }

        // Replace ", FMI " with " (FMI " and add a closing parenthesis
        return input.replace("(", "").replace(", FMI ", " (FMI ");
    }

    getFriendlyCodesString(records: IDiagnosticRecord[]): string {
        return records.map(x => this.reformatSPNCode(x.FriendlyCode)).join(", ");
    }

    // ---

    private getEmptyDiagnosticRecords(vehicle: IVehicle): IVehicleDiagnosticRecords {
        return {
            Id: vehicle.Id,
            vehicle,
            activeRecords: [],
            lastRecord: null,
            tagIds: []
        }
    }

    private findAndAddRecordsForVehicle(item: IVehicleDiagnosticRecords, activeRecords: IDiagnosticRecord[]): void {
        const activeRecordsForVehicle = activeRecords.filter(x => x.VehicleId === item.Id);
        item.activeRecords.push(...activeRecordsForVehicle);
    }

    private findAndAssignLastRecord(item: IVehicleDiagnosticRecords, lastRecords: IDiagnosticRecord[]): void {
        const matchingRecords = lastRecords.filter(x => x.VehicleId === item.Id);

        if (matchingRecords.length > 0) {
            item.lastRecord = matchingRecords[matchingRecords.length - 1];
        }
    }
}
